import * as React from 'react';
import { TranslateContext } from './context.js';

/**
 * Retrieve translate function primed with keys returned by keys factory.
 *
 * Use key factory only for dynamically created keys. Static keys must be
 * fetched with Loader.preload
 *
 *
 * @example // Usage with static keys
 * function Component() {
 *   const translate = useTranslate()
 *   return <div>{translate('some-key')}</div>
 * }
 *
 * Herz.I18n.Loader.preload(['some-key'], Component)
 *
 * @example // Usage with dynamic keys
 * function Component(props) {
 *   const translate = useTranslate(
 *     () => [`page.${props.slug}.title`],
 *     [props.slug]
 *   )
 *   return <div>{translate(`page.${props.slug}.title`)}</div>
 * }
 *
 * @param {Function} keysFactory
 *   Function that returns array of keys needed for component to render.
 *
 * @param {?Array.<*>} deps=[]
 *   Array of values used to determine if keys must be
 *   recomputed. Defaults to empty array which will force factory to be called
 *   only once.
 *
 * @returns {Function}
 *
 * @see {@link https://reactjs.org/docs/hooks-reference.html#usememo}
 *
 * @access public
 */

function useTranslate(keysFactory) {
  var deps = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var getTranslate = React.useContext(TranslateContext);
  var keys = React.useMemo(() => {
    if (!keysFactory) {
      return;
    }

    return keysFactory();
  }, deps);
  return React.useMemo(() => getTranslate(keys), [getTranslate, keys]);
}

export { useTranslate };
